import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldAlert, Smartphone, Wrench } from "lucide-react";
import ServicesSection from "@/components/ServicesSection";
import SectionHeader from "@/components/SectionHeader";
import { ProductCard } from "@/components/ProductCard";
import { useProducts, useServices } from "@/hooks/useCatalog";
import { useSeo } from "@/lib/seo";

export function ServicesPage() {
  const { data: services = [] } = useServices();
  useSeo({
    title: "Nos services informatiques — ISMO-TECH",
    description:
      "Installation, assistance, diagnostic, configuration et services professionnels. Déblocage et récupération uniquement autorisés.",
  });

  return (
    <div className="pb-6">
      <section className="hero-shell relative overflow-hidden">
        <div className="hero-grid-lines absolute inset-0 opacity-60" />
        <div className="container-it relative z-10 py-12 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Assistance autorisée</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight text-white sm:text-5xl" data-testid="services-title">
            Des services informatiques <span className="text-brand">clairs et encadrés</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Installation, configuration, diagnostic et assistance : nos techniciens interviennent sur vos appareils
            dans le respect des règles et des licences.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/support#ticket"
              data-testid="services-cta-ticket"
              className="btn-glow rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white"
            >
              Demander une intervention
            </Link>
            <Link
              to="/deblocage"
              className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10"
            >
              Déblocage & Services
            </Link>
          </div>
        </div>
      </section>

      <section className="container-it py-12 lg:py-20">
        <SectionHeader title="Nos services" subtitle="Ce que nous faisons" testId="services-list-title" />
        <ServicesSection services={services} />
      </section>

      <section className="container-it pb-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <p className="flex items-center gap-2 font-display text-base font-bold text-amber-900">
            <ShieldAlert className="h-5 w-5" /> Notre cadre d'intervention
          </p>
          <p className="mt-2 text-sm leading-relaxed text-amber-800">
            ISMO-TECH ne propose aucun outil ni service permettant de contourner illégalement des protections, des
            licences, des comptes ou des dispositifs de sécurité. Les prestations de déblocage ou de récupération sont
            réalisées uniquement lorsque le client possède légitimement l'appareil ou le compte concerné, sur
            présentation d'une preuve de propriété.
          </p>
        </div>
      </section>
    </div>
  );
}

const STEPS = [
  { title: "1. Vérification", text: "Vous fournissez l'IMEI/le numéro de série et une preuve de propriété." },
  { title: "2. Validation", text: "Notre équipe confirme l'éligibilité de l'appareil et le délai." },
  { title: "3. Traitement", text: "La demande est transmise par les canaux autorisés (24 à 72 h)." },
  { title: "4. Confirmation", text: "Vous recevez la confirmation et les instructions finales." },
];

export function UnlockPage() {
  const { data } = useProducts({ category: "deblocage-services", limit: 12 });
  useSeo({
    title: "Déblocage & Services iPhone / Android — ISMO-TECH",
    description:
      "Logiciels professionnels et services de déblocage autorisés pour iPhone et Android : diagnostic, réparation logicielle, déverrouillage opérateur sur preuve de propriété.",
  });

  return (
    <div className="pb-6">
      <section className="hero-shell relative overflow-hidden">
        <div className="hero-grid-lines absolute inset-0 opacity-60" />
        <div className="container-it relative z-10 grid items-center gap-8 py-12 lg:grid-cols-2 lg:py-20">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand">
              <Smartphone className="h-4 w-4" /> iPhone & Android
            </p>
            <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight text-white sm:text-5xl" data-testid="unlock-title">
              Déblocage & services <span className="text-brand">autorisés</span>
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
              Logiciels professionnels pour techniciens et prestations de déverrouillage opérateur réalisées
              uniquement pour le propriétaire légitime de l'appareil.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/support#ticket"
                data-testid="unlock-cta"
                className="btn-glow rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white"
              >
                Vérifier mon éligibilité
              </Link>
              <a
                href="#outils"
                className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10"
              >
                Voir les outils
              </a>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {STEPS.map((step) => (
              <div key={step.title} className="glass-dark rounded-2xl p-5">
                <p className="font-display text-sm font-bold text-white">{step.title}</p>
                <p className="mt-1.5 text-xs leading-snug text-slate-300">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-it py-12 lg:py-16" id="outils">
        <SectionHeader
          title="Outils & prestations disponibles"
          subtitle="Catalogue déblocage"
          linkTo="/boutique?categorie=deblocage-services"
          testId="unlock-products-title"
        />
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {(data?.items || []).map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </section>

      <section className="container-it pb-6">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-line bg-white p-6">
            <p className="flex items-center gap-2 font-display text-base font-bold text-ink">
              <Wrench className="h-5 w-5 text-brand" /> Ce que nous faisons
            </p>
            <ul className="mt-4 space-y-2.5">
              {[
                "Diagnostic logiciel et réparation système",
                "Flashage et restauration de firmware",
                "Déverrouillage opérateur via les canaux autorisés",
                "Configuration et migration de données",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6">
            <p className="flex items-center gap-2 font-display text-base font-bold text-rose-900">
              <ShieldAlert className="h-5 w-5" /> Ce que nous ne faisons pas
            </p>
            <ul className="mt-4 space-y-2.5">
              {[
                "Contournement illégal de protections ou de licences",
                "Déblocage d'appareils déclarés perdus ou volés",
                "Accès à un compte dont vous n'êtes pas le titulaire",
                "Suppression de sécurités sans preuve de propriété",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-rose-800">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
